import { h, Fragment } from 'preact';
import { useCallback } from 'preact/hooks';
import { useDispatch, useSelector } from 'react-redux';

import { Translate } from '@travel/translation';
import { useDeviceTypes } from '@travel/traveler-core/hooks';
import { getDefaultURLSearchRequest } from '@travel/traveler-core/utils/jrdpHelper';
import LoadingCircle from '@travel/ui/components/FlatButton/loadingCircle';
import isNotEmptyArray from '@travel/ui/utils/arrays';

import EmptyState from 'components/EmptyState';
import ProviderCard from 'components/HotelMap/components/ProviderCard';
import PaginationButton from 'components/PaginationButton';
import ProviderListSkeleton from 'components/ProviderListSkeleton';
import { fetchMoreHotels } from 'store/hotelList/actions';
import { getIsFetching, getIsFetchingMoreHotels, hasMoreHotel } from 'store/hotelList/selectors';
import { getIsFetchingMapList } from 'store/hotelListMap/selectors';

import paths from 'constants/paths';
import { getDefaultURLCustomListRequest } from 'utils/search';

import styles from '../../hotelListMap.module.scss';

import type { Area, URLSearchQuery } from 'Common-Types';
import type { HotelList, RequestBody } from 'HotelList-Types';
type Props = {
  hotels: HotelList;
  pageIdentifier: string;
  pageNumber?: number;
  accommodationAreaPath: string;
  accommodations: Array<Area>;
  urlSearchQuery?: URLSearchQuery;
  onHoverProviderCard?: (providerId) => void;
};

function List({
  hotels,
  pageIdentifier,
  pageNumber,
  urlSearchQuery,
  accommodationAreaPath,
  accommodations,
  onHoverProviderCard,
}: Props) {
  const hasShowMore = useSelector(hasMoreHotel);
  const isFetchingHotelList = useSelector(getIsFetching);
  const isFetchingMapList = useSelector(getIsFetchingMapList);
  const isFetchingMoreHotels = useSelector(getIsFetchingMoreHotels);

  const hotelListMapUrl = paths.providerListMap.pathResolver(accommodationAreaPath);

  const { isPC } = useDeviceTypes();
  const dispatch = useDispatch();
  const handlePagination = useCallback(() => {
    const query = {
      ...getDefaultURLSearchRequest(urlSearchQuery),
      ...getDefaultURLCustomListRequest(urlSearchQuery),
      latitude: urlSearchQuery?.latitude,
      longitude: urlSearchQuery?.longitude,
      zoomLevel: urlSearchQuery?.zoomLevel,
      accommodationArea: pageIdentifier,
    } as RequestBody;

    dispatch(fetchMoreHotels(query));
  }, [pageIdentifier, dispatch, urlSearchQuery]);

  if (isFetchingMapList || isFetchingHotelList) {
    return (
      <div class={styles.mapList}>
        <ProviderListSkeleton isPC={isPC} />
        <ProviderListSkeleton isPC={isPC} />
        {!isPC && <ProviderListSkeleton isPC={isPC} />}
        <div class={styles.loading}>
          <LoadingCircle classType="primary" isLoading />
        </div>
      </div>
    );
  }

  return (
    <section
      className={styles.mapList}
      data-testid="List-listContainer"
      onMouseLeave={() => onHoverProviderCard && onHoverProviderCard(null)}
    >
      {isNotEmptyArray(hotels.providers) ? (
        <>
          {hotels.providers.map(hotel => (
            <ProviderCard
              hotelName={hotel.name}
              hotel={hotel}
              key={hotel.id}
              type="mapLeftCard"
              searchQuery={urlSearchQuery}
              onMouseEnter={onHoverProviderCard}
              accommodationAreaPathId={accommodationAreaPath}
              accommodations={accommodations}
            />
          ))}
          {isFetchingMoreHotels ? (
            <div class={styles.loading}>
              <LoadingCircle classType="primary" isLoading />
            </div>
          ) : (
            hasShowMore && (
              <PaginationButton
                className={styles.seeMoreButton}
                limit={hotels.limit}
                offset={hotels.offset}
                total={hotels.total}
                onClick={handlePagination}
                to={hotelListMapUrl}
                pageNumber={pageNumber}
              />
            )
          )}
        </>
      ) : (
        <EmptyState
          title={<Translate id="Hotel_List.No_Results.Sorry_Message" />}
          description={<Translate id="Hotel_List.No_Results.Change_Condition" />}
        />
      )}
    </section>
  );
}

export default List;
